import { useUpdateInboxItem } from "@/api/inbox";
import * as analytics from "@/utils/analytics";
import { InboxItem } from "@cohesive/api-client";
import { useState } from "react";

export interface UseInboxItemSelectionOptions {
  items: InboxItem[];
}

export interface UseInboxItemSelectionReturn {
  // State
  selectedItems: Set<string>;

  // Selection handlers
  handleSelectionChange: (selected: boolean, item: InboxItem) => void;
  handleClearSelection: () => void;

  // Bulk actions
  handleBulkStar: () => void;
  handleBulkArchive: () => void;

  // Helper functions
  isItemSelected: (itemId: string) => boolean;
  hasSelectedItems: boolean;
  selectedCount: number;

  // Group selection helpers
  areAllGroupItemsSelected: (groupItems: InboxItem[]) => boolean;
  toggleGroupSelection: (groupItems: InboxItem[]) => void;

  // Selection state helpers
  selectedItemsStarred: "all" | "none" | "mixed";
  selectedItemsArchived: "all" | "none" | "mixed";
}

export function useInboxItemSelection({
  items,
}: UseInboxItemSelectionOptions): UseInboxItemSelectionReturn {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const { mutate: updateInboxItem } = useUpdateInboxItem();

  const handleSelectionChange = (selected: boolean, item: InboxItem) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(item.id);
      } else {
        newSet.delete(item.id);
      }
      return newSet;
    });
  };

  const handleBulkStar = () => {
    const selectedItemsArray = Array.from(selectedItems);
    const allItemsStarred = selectedItemsArray.every((itemId) => {
      const item = items.find((i) => i.id === itemId);
      return item?.is_starred ?? false;
    });
    selectedItemsArray.forEach((itemId) => {
      const item = items.find((i) => i.id === itemId);
      if (item) {
        updateInboxItem({
          id: itemId,
          data: { is_starred: allItemsStarred ? false : true },
        });
        analytics.trackInboxItemStarred({ inboxItem: item });
      }
    });

    setSelectedItems(new Set()); // Clear selection
  };

  const handleBulkArchive = () => {
    const selectedItemsArray = Array.from(selectedItems);
    const allItemsArchived = selectedItemsArray.every((itemId) => {
      const item = items.find((i) => i.id === itemId);
      return item?.is_archived ?? false;
    });
    selectedItemsArray.forEach((itemId) => {
      const item = items.find((i) => i.id === itemId);
      if (item) {
        updateInboxItem({
          id: itemId,
          data: { is_archived: allItemsArchived ? false : true },
        });
        analytics.trackInboxItemArchived({ inboxItem: item });
      }
    });

    setSelectedItems(new Set()); // Clear selection
  };

  const handleClearSelection = () => {
    setSelectedItems(new Set());
  };

  const isItemSelected = (itemId: string): boolean => {
    return selectedItems.has(itemId);
  };

  const areAllGroupItemsSelected = (groupItems: InboxItem[]): boolean => {
    return groupItems.every((item) => selectedItems.has(item.id));
  };

  const toggleGroupSelection = (groupItems: InboxItem[]) => {
    const allSelected = areAllGroupItemsSelected(groupItems);

    setSelectedItems((prev) => {
      const newSet = new Set(prev);

      if (allSelected) {
        // Deselect all items in group
        groupItems.forEach((item) => newSet.delete(item.id));
      } else {
        // Select all items in group
        groupItems.forEach((item) => newSet.add(item.id));
      }

      return newSet;
    });
  };

  // Calculate selection states for starred and archived items
  const getSelectedItemsStarredState = (): "all" | "none" | "mixed" => {
    if (selectedItems.size === 0) return "none";

    const selectedItemsArray = Array.from(selectedItems);
    const starredCount = selectedItemsArray.reduce((count, itemId) => {
      const item = items.find((i) => i.id === itemId);
      return count + (item?.is_starred ? 1 : 0);
    }, 0);

    if (starredCount === 0) return "none";
    if (starredCount === selectedItemsArray.length) return "all";
    return "mixed";
  };

  const getSelectedItemsArchivedState = (): "all" | "none" | "mixed" => {
    if (selectedItems.size === 0) return "none";

    const selectedItemsArray = Array.from(selectedItems);
    const archivedCount = selectedItemsArray.reduce((count, itemId) => {
      const item = items.find((i) => i.id === itemId);
      return count + (item?.is_archived ? 1 : 0);
    }, 0);

    if (archivedCount === 0) return "none";
    if (archivedCount === selectedItemsArray.length) return "all";
    return "mixed";
  };

  return {
    // State
    selectedItems,

    // Selection handlers
    handleSelectionChange,
    handleClearSelection,

    // Bulk actions
    handleBulkStar,
    handleBulkArchive,

    // Helper functions
    isItemSelected,
    hasSelectedItems: selectedItems.size > 0,
    selectedCount: selectedItems.size,

    // Group selection helpers
    areAllGroupItemsSelected,
    toggleGroupSelection,

    // Selection state helpers
    selectedItemsStarred: getSelectedItemsStarredState(),
    selectedItemsArchived: getSelectedItemsArchivedState(),
  };
}
